import { ProfileRole, ProfileRoleMap } from "@qendo/profile";
import { OptionType } from "@qendo/types";
import { ucfirst } from "@samlevy/string-helpers";
import { Stack, useLocalSearchParams } from "expo-router";
import { useMemo, useState } from "react";
import { View } from "react-native";
import { Button } from "~/components/Button";
import { Badge } from "~/components/feedback/Badge";
import { HeaderButton } from "~/components/HeaderButton";
import { Avatar } from "~/components/layout/Avatar";
import { DataList } from "~/components/layout/DataList";
import { DataListItem } from "~/components/layout/DataListItem";
import { ConfirmationModal } from "~/components/layout/modal/ConfirmationModal";
import { Page } from "~/components/layout/Page";
import { Spacer } from "~/components/layout/Spacer";
import { PermissionsOutput } from "~/components/layout/team/PermissionsOutput";
import { Subtitle, Title } from "~/components/layout/text";
import { Authorize } from "~/components/permission/Authorize";
import { Dismiss } from "~/components/permission/Dismiss";
import { Flex, Gap, Items, Width } from "~/constants/layout";
import { useFeedback } from "~/providers/FeedbackProvider";
import { useOptions } from "~/providers/OptionsProvider";
import { useProfile } from "~/providers/ProfileProvider";
import { useUser } from "~/providers/UserProvider";
import { filled } from "~/util";
import { isContact, isEmergencyContact, isInvitation, isShare, isTeamMember, isUserRole } from "~/util/qendo/team";

export default function ShowTeam() {
    const { team_member_id }: { team_member_id: string } = useLocalSearchParams();
    const { user } = useUser();
    const { findTeam, profile_id, canUpdateTeamMember, destroyTeamMember, destroyContact, destroyInvitation } =
        useProfile();
    const { addToast } = useFeedback();
    const [confirming, setConfirming] = useState<boolean>(false);
    const [deleting, setDeleting] = useState(false);
    const { findOption } = useOptions();

    const member = useMemo(() => findTeam(team_member_id), [findTeam, team_member_id]);

    if (!member) {
        return <Dismiss />;
    }

    const role = member.role;
    const is_user_role = isUserRole(role);
    const is_contact = isContact(member);
    const is_emergency_contact = isEmergencyContact(member);
    const is_invite = isInvitation(member);
    const is_user = isTeamMember(member);
    const is_share = isShare(member);
    const is_primary_user = is_user && member.user?.profile_id === profile_id;
    const option = findOption(OptionType.team_members, member.option_id);

    const subtitle = is_user_role
        ? [ProfileRoleMap[role], is_user && user.id === member.user.id && "You"].filter(filled).join(" - ")
        : "";

    const can_modify = canUpdateTeamMember(team_member_id);

    const title = (is_contact && "contact") || (is_invite && "invite") || "team member";

    const doDelete = () => {
        if (!can_modify || deleting) {
            return;
        }

        setDeleting(true);

        const action = (is_contact && destroyContact) || (is_invite && destroyInvitation) || destroyTeamMember;

        action(team_member_id)
            .then(() => {
                addToast({ content: `${ucfirst(title)} removed.` });
            })
            .finally(() => {
                setConfirming(false);
                setDeleting(false);
            });
    };

    return (
        <Authorize ability="view" section="team">
            <Stack.Screen
                options={{
                    headerRight: () =>
                        can_modify && (
                            <HeaderButton
                                href={{ pathname: "/more/care-team/[team_member_id]/edit", params: { team_member_id } }}
                                title="Edit"
                            />
                        ),
                }}
            />
            <Page spacer>
                <View style={[Items.start, Gap.sm]}>
                    {is_emergency_contact && (
                        <Badge title="Emergency Contact" icon="star-of-life" size="sm" variant="danger" />
                    )}
                    <View style={[Flex.row, Gap.sm, Items.center, Width.full]}>
                        {is_user && !is_share && <Avatar target={member.user} />}
                        <View style={[Items.start, Gap.xxx]}>
                            <Title>{member.name}</Title>
                            {(is_primary_user && <Badge title="Primary user" size="sm" />) ||
                                (is_share && <Badge title="Shared" size="sm" variant="secondary" />) ||
                                (filled(subtitle) && <Subtitle>{subtitle}</Subtitle>)}
                        </View>
                    </View>
                </View>
                <DataList>
                    {is_share || <DataListItem title="Email" data={member.email} />}
                    {filled(member.phone) && <DataListItem title="Phone" data={member.phone} />}
                    {filled(option) && <DataListItem title="Relationship" data={option.title} />}
                    {(is_user_role || is_share) && (
                        <DataListItem title="Access level">
                            {(role === ProfileRole.owner &&
                                "Owner - this person has full access to your entire account.") ||
                                (role === ProfileRole.support &&
                                    "Support - this person has read-only access to your entire account.") ||
                                (role === ProfileRole.medical &&
                                    "Medical - this person has read-only access to your health profile and logs") ||
                                "Share - this person has read-only access to the selected content."}
                        </DataListItem>
                    )}
                </DataList>
                {is_invite && <Badge icon="info-circle" title="Invite not yet accepted" variant="info" />}
                {is_share && <PermissionsOutput permissions={member.permissions} role={ProfileRole.share} />}
                {can_modify && (
                    <>
                        <Spacer />
                        <View style={Items.center}>
                            <Button
                                icon="trash"
                                title={`Delete ${title}`}
                                variant="destructive"
                                size="lg"
                                onPress={() => setConfirming(true)}
                            />
                        </View>
                    </>
                )}
            </Page>
            {confirming && (
                <ConfirmationModal
                    onConfirm={doDelete}
                    title={`Are you sure you want to delete this ${title}?`}
                    confirmText={`Delete ${title}`}
                    confirmVariant={"destructive-quiet"}
                    loading={deleting}
                    onRequestClose={() => setConfirming(false)}
                />
            )}
        </Authorize>
    );
}
